'use client';

/**
 * useBoatBooking — submit prenotazione condiviso (catalogo + detail).
 * 100% in catalogo: auth (login solo qui, returnUrl torna indietro) → createBooking →
 * initiatePayment (Mollie) → redirect. Riusa calculatePriceBreakdown (stessa util del detail)
 * per parità col path testato. ⚠️ Mollie LIVE in prod → testare solo simulato su staging.
 */

import { useRouter } from 'next/navigation';
import { useAuth } from '@/app/providers';
import { createBooking } from '@/services/bookingService';
import { initiatePayment } from '@/services/paymentService';
import { calculatePriceBreakdown, type ExtraPricing } from '@/lib/booking-utils';
import type { Boat } from '@/types';

export interface BoatBookingData {
  guests: number;
  selectedExtras: string[];
  startTime: string;
  endTime: string;
  dayParts?: Record<string, 'full' | 'morning' | 'afternoon'>;
}

const toDateStr = (d: Date): string =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

export function useBoatBooking() {
  const router = useRouter();
  const { user } = useAuth();

  const submit = async (boat: Boat, start: Date, end: Date, data: BoatBookingData) => {
    // Login richiesto solo qui (al submit). returnUrl torna al catalogo.
    if (!user) {
      const ret = typeof window !== 'undefined' ? window.location.pathname + window.location.search : '/barche';
      router.push(`/login?returnUrl=${encodeURIComponent(ret)}`);
      return { needLogin: true };
    }

    const boatExtras: ExtraPricing[] = (boat.extras || []).map((e) => ({
      id: e.id, price: Number(e.price) || 0, priceType: e.priceType || 'per_stay',
    }));
    const mandatoryIds = (boat.extras || []).filter((e) => e.mandatory).map((e) => e.id);
    const selectedWithMandatory = [...data.selectedExtras];
    for (const id of mandatoryIds) if (!selectedWithMandatory.includes(id)) selectedWithMandatory.push(id);

    const breakdown = calculatePriceBreakdown(
      start, end, data.startTime, data.endTime,
      boat.price, boat.price_half_day,
      boatExtras, selectedWithMandatory, data.guests,
      undefined, 0.3, [],
      boat.shortStaySurcharge || 0, boat.rentalMode || 'daily',
    );

    const nights = breakdown.fullDays + breakdown.halfDays;
    const grand = breakdown.baseTotal + breakdown.extrasTotal + (breakdown.shortStaySurchargeAmount || 0);

    const result = await createBooking({
      boatId: String(boat.id),
      guestId: user.id,
      checkIn: toDateStr(start),
      checkOut: toDateStr(end),
      checkInTime: data.startTime,
      checkOutTime: data.endTime,
      guestsCount: data.guests,
      nights,
      basePrice: breakdown.baseTotal,
      extrasTotal: breakdown.extrasTotal,
      shortStaySurchargeAmount: breakdown.shortStaySurchargeAmount || undefined,
      shortStaySurchargePercent: breakdown.shortStaySurchargePercent || undefined,
      totalPrice: Number(grand.toFixed(2)),
      selectedExtras: breakdown.extrasBreakdown,
      paymentMethod: 'CREDIT_CARD',
      dayParts: data.dayParts,
    });

    if (result.data?.id) {
      const { paymentUrl } = await initiatePayment(result.data.id);
      window.location.href = paymentUrl;
    }
    return { ok: true };
  };

  return { submit, isLoggedIn: !!user };
}
